import { useQuery } from '@tanstack/react-query';
import { useEffect, type JSX } from 'react';
import { Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { PermissionPicker } from '@/components/roles/PermissionPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useZodForm } from '@/lib/forms/useZodForm';
import { getPermissionsRequest } from '@/services/permissions';
import type { PermissionsResponse } from '@/types/permissions';

/** Inferred form values for both create and edit modes. */
const roleFormSchema = z.object({
  name: z
    .string()
    .min(3, 'At least 3 characters')
    .max(50, 'At most 50 characters')
    .regex(/^[A-Za-z0-9 -]+$/, 'Only letters, digits, spaces, hyphens'),
  description: z.string().max(500, 'At most 500 characters').nullable().optional(),
  permissionCodes: z.array(z.string()).min(1, 'Select at least one permission'),
  isActive: z.boolean(),
});

export type RoleFormValues = z.infer<typeof roleFormSchema>;

export interface RoleFormProps {
  readonly initialValues?: Partial<RoleFormValues>;
  readonly mode: 'create' | 'edit';
  readonly onSubmit: (values: RoleFormValues) => Promise<void> | void;
  readonly submitting?: boolean;
  readonly errorMessage?: string | null;
}

const EMPTY_DEFAULTS: RoleFormValues = {
  name: '',
  description: '',
  permissionCodes: [],
  isActive: true,
};

export function RoleForm({
  initialValues,
  mode,
  onSubmit,
  submitting = false,
  errorMessage,
}: RoleFormProps): JSX.Element {
  const defaults: RoleFormValues = {
    ...EMPTY_DEFAULTS,
    ...initialValues,
    permissionCodes: initialValues?.permissionCodes ? [...initialValues.permissionCodes] : [],
  };

  const form = useZodForm({
    schema: roleFormSchema,
    defaultValues: defaults,
  });

  // Re-sync defaults when initialValues changes (RoleEditPage hydrates async).
  useEffect(() => {
    if (initialValues !== undefined) {
      form.reset({
        ...EMPTY_DEFAULTS,
        ...initialValues,
        permissionCodes: initialValues.permissionCodes ? [...initialValues.permissionCodes] : [],
      });
    }
    // form.reset is stable; intentionally only depend on initialValues identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const permissionsQuery = useQuery<PermissionsResponse>({
    queryKey: ['permissions'],
    queryFn: getPermissionsRequest,
  });

  return (
    <form
      onSubmit={(e) => {
        void form.handleSubmit(onSubmit)(e);
      }}
      className="space-y-6"
      noValidate
      data-testid={`role-form-${mode}`}
    >
      {errorMessage != null && errorMessage !== '' && (
        <div
          role="alert"
          data-testid="role-form-error"
          className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="role-name">Role name</Label>
        <Input
          id="role-name"
          type="text"
          autoComplete="off"
          disabled={submitting}
          aria-invalid={form.formState.errors.name !== undefined}
          {...form.register('name')}
          data-testid="role-form-name"
        />
        {form.formState.errors.name && (
          <p className="text-xs text-destructive" data-testid="role-form-name-error">
            {form.formState.errors.name.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="role-description">Description (optional)</Label>
        <Input
          id="role-description"
          type="text"
          autoComplete="off"
          disabled={submitting}
          aria-invalid={form.formState.errors.description !== undefined}
          {...form.register('description')}
          data-testid="role-form-description"
        />
        {form.formState.errors.description && (
          <p className="text-xs text-destructive" data-testid="role-form-description-error">
            {form.formState.errors.description.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Permissions</Label>
        {permissionsQuery.isLoading && (
          <p className="text-sm text-muted-foreground">Loading permissions…</p>
        )}
        {permissionsQuery.isError && (
          <p className="text-sm text-destructive">Failed to load permissions registry.</p>
        )}
        {permissionsQuery.data && (
          <Controller
            control={form.control}
            name="permissionCodes"
            render={({ field }) => (
              <PermissionPicker
                value={field.value}
                onChange={field.onChange}
                permissions={permissionsQuery.data}
                disabled={submitting}
              />
            )}
          />
        )}
        {form.formState.errors.permissionCodes && (
          <p className="text-xs text-destructive" data-testid="role-form-permissions-error">
            {form.formState.errors.permissionCodes.message}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          id="role-isactive"
          type="checkbox"
          disabled={submitting}
          {...form.register('isActive')}
          data-testid="role-form-isactive"
          className="h-4 w-4 rounded border-input"
        />
        <Label htmlFor="role-isactive">Active</Label>
      </div>

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting} data-testid="role-form-submit">
          {submitting
            ? mode === 'create'
              ? 'Creating…'
              : 'Saving…'
            : mode === 'create'
              ? 'Create role'
              : 'Save changes'}
        </Button>
        <Button asChild variant="outline" type="button">
          <Link to="/roles" data-testid="role-form-cancel">
            Cancel
          </Link>
        </Button>
      </div>
    </form>
  );
}
